import { AIAssistantAbstraction, withCommonAIAssistant } from './AIAssistantAbstract';
import React from 'react';
import { ToxnotServiceClient } from '../../store/ToxnotServiceClient';
import { applyExchangeAssistantTypographyOverride } from './AIAssistantTypographyOverride';

interface AIAssistantHomeState {
    encryptedUserName: string | null;
    email: string | null;
    isLoadingEncryptedUser: boolean;
}

class AIAssistantHome extends AIAssistantAbstraction {
    private readonly _serviceClient: ToxnotServiceClient;
    private readonly _assistantElementRef = React.createRef<HTMLElement>();
    private _assistantTypographyOverrideTimer: number | null = null;
    state: AIAssistantHomeState = {
        encryptedUserName: null,
        email: null,
        isLoadingEncryptedUser: true
    };

    constructor(props: any) {
        super(props);
        this._serviceClient = new ToxnotServiceClient();
    }

    componentDidMount() {
        // Load the script after the component mounts
        this.loadInsightChatGptScript();
        
        // Get encrypted username before component renders
        //The componentDidMount method calls getEncryptedUserName which sets state, but this component render before the state is updated, This behavior is handled under getEncryptedUserName.
        this.getEncryptedUserName();
    }

    componentDidUpdate() {
        this.applyAssistantTypographyOverride();
    }

    componentWillUnmount() {
        if (this._assistantTypographyOverrideTimer !== null) {
            window.clearTimeout(this._assistantTypographyOverrideTimer);
        }
    }

    private async getEncryptedUserName() {
        //User object or username is never null/undefined
        const userObject = this.getUserObject();
        
        if (userObject.userName && !this.state.encryptedUserName) {
            // Don't check isLoadingEncryptedUser here since we want to allow the initial load
            this.setState({ isLoadingEncryptedUser: true });
            
            try {
                const result = await this._serviceClient.getEncryptedUserFromCompli(userObject.userName);
                // Cast result to any to access data property, as the backend returns ResourceDataResult<string>
                const resultWithData = result as any;
                if (result.success && resultWithData.data && resultWithData.data.userName) {
                    // The encrypted username is returned in the result.data field
                    this.setState({ encryptedUserName: resultWithData.data.userName, email: resultWithData.data.emailId });
                } else {
                    console.error('Failed to get encrypted username from Compli API:', result.result);
                    this.setState({ encryptedUserName: userObject.userName, email: userObject.email });
                }
            } catch (error) {
                console.error('Error calling Compli API to encrypt username:', error);
                this.setState({ encryptedUserName: userObject.userName, email: userObject.email });
            } finally {
                this.setState({ isLoadingEncryptedUser: false });
            }
        }
    }

    private loadInsightChatGptScript() {
        const scriptUrl = `/mfe/ai-assistant/main.js`;
        
        // Check if script is already loaded to avoid duplicates
        if (document.querySelector(`script[src="${scriptUrl}"]`)) {
            return;
        }

        const script = document.createElement('script');
        script.defer = true;
        script.type = 'module';
        script.src = scriptUrl;

        // Add the script to the document head
        document.head.appendChild(script);
    }

    private applyAssistantTypographyOverride(retryCount: number = 0) {
        const didApplyShadowOverride = applyExchangeAssistantTypographyOverride(this._assistantElementRef.current);

        if (!didApplyShadowOverride && retryCount < 20) {
            this._assistantTypographyOverrideTimer = window.setTimeout(() => this.applyAssistantTypographyOverride(retryCount + 1), 100);
        }
    }

    protected renderAIAssistantContent(): JSX.Element {
        const userObject = this.getUserObject();
        const userContext = this.getUserContextForSignedInUser();

        if (this.state.isLoadingEncryptedUser || !this.state.email) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <div>Loading Portfolio Assistant...</div>
                </div>
            );
        }

        const config = this.props.appServices.config;

        // Use the encrypted username if available, otherwise use the original username
        const userObjectForAI = {
            userName: this.state.encryptedUserName || userObject.userName,
            token: ""
        };
        const metadata = {
            persist: {
                team_owner_id: userContext.teamOwnerId,
                identity_id: userContext.userId,
                identity_type: "0"
            },
            passThrough: {}
        };
        const assistantHostStyle: React.CSSProperties = {
            fontSize: '16px'
        };
        
        return (
            //<ai-assistant-mfe user={JSON.stringify(userObjectForAI)} app-name="exchange" settings={JSON.stringify(settingsConfig)} data-mfe="Exchange"></ai-assistant-mfe>
            <ai-assistant-mfe ref={this._assistantElementRef} user={JSON.stringify(userObjectForAI)} app-name="exchange" account-id={userContext.teamOwnerId} api-url={config.aiAssistantBffBaseUrl} email-id={this.state.email} websocket-url={config.toxnotWebAppUrlEndpoint + "/bff/assistant/ws"} metadata={JSON.stringify(metadata)} data-mfe="" style={assistantHostStyle}>
            </ai-assistant-mfe>
        );
    }
}

export default withCommonAIAssistant(AIAssistantHome);
